import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import { LEGACY_RUNTIME_CONFIG_DEFAULTS } from './runtime-config.defaults';
import {
  RuntimeConfig,
  RuntimeConfigOverrides,
  RuntimeEnvironmentTarget
} from './runtime-config.model';
import { RUNTIME_CONFIG } from './runtime-config.token';
import { environment } from '../../../environments/environment';

const WINDOW_CONFIG_KEY = '__WEB_MONITOR_RUNTIME_CONFIG__' as const;
export const RUNTIME_ENVIRONMENT_OVERRIDE_STORAGE_KEY =
  'monitoring_runtime_environment_target_v1' as const;

const RUNTIME_ENVIRONMENT_ENDPOINTS: Record<RuntimeEnvironmentTarget, RuntimeConfigOverrides> = {
  prod: {
    environment: {
      target: 'prod'
    },
    backend: {
      baseUrl: 'https://api-monitor.ggtaxi.com/'
    },
    realtime: {
      baseUrl: 'https://api-monitor.ggtaxi.com/',
      monitorPath: 'monitor'
    }
  },
  dev: {
    environment: {
      target: 'dev'
    },
    backend: {
      baseUrl: 'https://dev-api-monitor.team.gg/'
    },
    realtime: {
      baseUrl: 'https://dev-api-monitor.team.gg/',
      monitorPath: 'monitor'
    }
  },
  staging: {
    environment: {
      target: 'staging'
    },
    backend: {
      baseUrl: 'https://staging-api-monitor.team.gg/'
    },
    realtime: {
      baseUrl: 'https://staging-api-monitor.team.gg/',
      monitorPath: 'monitor'
    }
  }
};

export function provideRuntimeConfig(
  overrideConfig?: RuntimeConfigOverrides
): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: RUNTIME_CONFIG,
      useFactory: () =>
        resolveRuntimeConfig(
          overrideConfig ??
            mergeRuntimeConfigOverrides(environment.runtimeConfig, readWindowRuntimeConfig())
        )
    }
  ]);
}

export function resolveRuntimeConfig(
  overrides: RuntimeConfigOverrides = {}
): RuntimeConfig {
  const baseEnvironment = normalizeRuntimeEnvironmentTarget(
    overrides.environment?.target ?? environment.runtimeConfig.environment?.target
  );
  const selectedEnvironment =
    baseEnvironment === 'dev'
      ? normalizeRuntimeEnvironmentTarget(readStoredRuntimeEnvironmentTarget() ?? baseEnvironment)
      : baseEnvironment;
  const environmentOverrides = RUNTIME_ENVIRONMENT_ENDPOINTS[selectedEnvironment];
  const mergedOverrides = mergeRuntimeConfigOverrides(environmentOverrides, overrides);

  return {
    environment: {
      ...LEGACY_RUNTIME_CONFIG_DEFAULTS.environment,
      ...mergedOverrides.environment,
      target: selectedEnvironment
    },
    backend: {
      ...LEGACY_RUNTIME_CONFIG_DEFAULTS.backend,
      ...mergedOverrides.backend
    },
    realtime: {
      ...LEGACY_RUNTIME_CONFIG_DEFAULTS.realtime,
      ...mergedOverrides.realtime
    },
    maps: {
      ...LEGACY_RUNTIME_CONFIG_DEFAULTS.maps,
      ...mergedOverrides.maps
    },
    crm: {
      ...LEGACY_RUNTIME_CONFIG_DEFAULTS.crm,
      ...mergedOverrides.crm
    },
    assets: {
      ...LEGACY_RUNTIME_CONFIG_DEFAULTS.assets,
      ...mergedOverrides.assets,
      sounds: {
        ...LEGACY_RUNTIME_CONFIG_DEFAULTS.assets.sounds,
        ...mergedOverrides.assets?.sounds
      }
    },
    routing: {
      ...LEGACY_RUNTIME_CONFIG_DEFAULTS.routing,
      ...mergedOverrides.routing
    }
  };
}

export function readWindowRuntimeConfig(): RuntimeConfigOverrides {
  return window[WINDOW_CONFIG_KEY] ?? {};
}

export function readStoredRuntimeEnvironmentTarget(): RuntimeEnvironmentTarget | null {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return null;
  }

  const value = window.localStorage.getItem(RUNTIME_ENVIRONMENT_OVERRIDE_STORAGE_KEY);
  return value === null ? null : normalizeRuntimeEnvironmentTarget(value);
}

export function buildBackendUrl(config: RuntimeConfig, endpoint: string): string {
  return joinUrl(config.backend.baseUrl, endpoint);
}

export function buildRealtimeMonitorUrl(config: RuntimeConfig): string {
  return joinUrl(config.realtime.baseUrl, config.realtime.monitorPath);
}

export function normalizeRuntimeEnvironmentTarget(
  target: unknown
): RuntimeEnvironmentTarget {
  if (target === 'dev') return 'dev';
  if (target === 'staging') return 'staging';
  return 'prod';
}

export function buildCrmSearchUrl(config: RuntimeConfig, encodedQuery: string): string {
  return buildUrlWithQuery(config.crm.homeBaseUrl, encodedQuery);
}

export function buildGuaranteedShuttleUrl(
  config: RuntimeConfig,
  encodedQuery: string
): string {
  return buildUrlWithQuery(config.crm.guaranteedShuttlesBaseUrl, encodedQuery);
}

export function buildGooglePlacesScriptUrl(config: RuntimeConfig): string {
  const url = new URL(config.maps.googlePlacesScriptUrl);
  url.searchParams.set('key', config.maps.googlePlacesApiKey);

  if (!url.searchParams.has('libraries')) {
    url.searchParams.set('libraries', 'places');
  }

  return url.toString();
}

function buildUrlWithQuery(baseUrl: string, encodedQuery: string): string {
  const url = new URL(baseUrl);
  url.searchParams.set('q', encodedQuery);
  return url.toString();
}

function joinUrl(baseUrl: string, path: string): string {
  const normalizedBaseUrl = new URL(baseUrl, window.location.origin);

  if (isCanonicalMonitorHost(normalizedBaseUrl.hostname)) {
    normalizedBaseUrl.port = '';
  }

  const normalizedBase = normalizedBaseUrl.toString().endsWith('/')
    ? normalizedBaseUrl.toString()
    : `${normalizedBaseUrl.toString()}/`;
  const normalizedPath = path.replace(/^\/+/, '');
  if (!normalizedPath) {
    return normalizedBase;
  }

  return `${normalizedBase}${normalizedPath}`;
}

function isCanonicalMonitorHost(hostname: string): boolean {
  return hostname === 'api-monitor.ggtaxi.com' || hostname === 'socket-monitor.ggtaxi.com';
}

function mergeRuntimeConfigOverrides(
  base: RuntimeConfigOverrides,
  override: RuntimeConfigOverrides
): RuntimeConfigOverrides {
  return {
    environment: {
      ...base.environment,
      ...override.environment
    },
    backend: {
      ...base.backend,
      ...override.backend
    },
    realtime: {
      ...base.realtime,
      ...override.realtime
    },
    maps: {
      ...base.maps,
      ...override.maps
    },
    crm: {
      ...base.crm,
      ...override.crm
    },
    assets: {
      ...base.assets,
      ...override.assets,
      sounds: {
        ...base.assets?.sounds,
        ...override.assets?.sounds
      }
    },
    routing: {
      ...base.routing,
      ...override.routing
    }
  };
}
